import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API, TOKEN_KEY, LD_NAZIV_KEY, safeStorage } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Play } from "lucide-react";

/**
 * Deep-link demo entry point: /demo?section=<name>&role=<clan|predsednik>
 *
 * Samodejno izvede demo login, nato preusmeri na določeno sekcijo aplikacije.
 * Idealno za marketing objave, landing CTA-je, socialne omrežja.
 *
 * Primeri:
 *  /demo                     → dashboard, vloga: clan (privzeto)
 *  /demo?section=karta       → karta lovišča kot clan
 *  /demo?section=finance     → finance kot predsednik (samodejno)
 *  /demo?role=predsednik     → dashboard kot predsednik
 */

// Mapping: kratki alias → dejanska pot znotraj app
const SECTION_MAP = {
  dashboard: "/dashboard",
  home: "/dashboard",
  map: "/karta",
  karta: "/karta",
  finance: "/profil?tab=dolgovi",
  finances: "/profil?tab=dolgovi",
  dolgovi: "/profil?tab=dolgovi",
  events: "/dogodki",
  dogodki: "/dogodki",
  calendar: "/koledar",
  koledar: "/koledar",
  plan: "/plan-odstrela",
  odstrel: "/plan-odstrela",
  members: "/clani",
  clani: "/clani",
  announcements: "/obvestila",
  obvestila: "/obvestila",
  cabins: "/koce",
  koce: "/koce",
  hours: "/ure",
  ure: "/ure",
  weapons: "/orozje",
  orozje: "/orozje",
  "zgreseni-streli": "/zgreseni-streli",
  zgreseni: "/zgreseni-streli",
  "missed-shots": "/zgreseni-streli",
  training: "/izobrazevanje",
  izobrazevanje: "/izobrazevanje",
  cameras: "/kamere",
  kamere: "/kamere",
  ai: "/ai-ocena",
  messages: "/sporocila",
  sporocila: "/sporocila",
  files: "/datoteke",
  datoteke: "/datoteke",
  stats: "/statistike",
  statistike: "/statistike",
  profile: "/profil",
  profil: "/profil",
};

// Sekcije, ki so na voljo samo predsednikom → auto-promote na role=predsednik
const PRESIDENT_ONLY_SECTIONS = new Set([
  "stats", "statistike", "ure", "hours",
  "dostopi", "sms", "arhiv-prisotnosti", "email-nastavitve",
  "dnevnik", "vloge", "izvozi",
]);

const DemoLanding = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("Pripravljam demo dostop…");

  const rawSection = (params.get("section") || "dashboard").toLowerCase().trim();
  const rawRole = (params.get("role") || "").toLowerCase().trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Če je sekcija samo za predsednike, samodejno preklopi na predsednik-vlogo
      const needsPredsednik = PRESIDENT_ONLY_SECTIONS.has(rawSection);
      const role = rawRole === "predsednik" ? "predsednik"
                 : rawRole === "clan" ? "clan"
                 : (needsPredsednik ? "predsednik" : "clan");

      setStatus(`Vstopam kot ${role === "predsednik" ? "predsednik" : "član"}…`);
      try {
        const r = await axios.post(`${API}/demo/login`, { role });
        if (cancelled) return;
        const { token: newToken, user: userData } = r.data;
        safeStorage.setItem(TOKEN_KEY, newToken);
        if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
        if (userData?.ld_naziv) safeStorage.setItem(LD_NAZIV_KEY, userData.ld_naziv);
        await login("", "", null, { skipRequest: true, prefetched: { token: newToken, user: userData } });

        // Preusmeri
        const targetPath = SECTION_MAP[rawSection] || "/dashboard";
        toast.success(`Demo · ${role === "predsednik" ? "predsednik" : "član"} → ${targetPath}`);
        navigate(targetPath, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setStatus("Napaka pri demo vstopu");
        toast.error(e.response?.data?.detail || "Napaka pri demo vstopu");
        setTimeout(() => navigate("/login"), 2000);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSection, rawRole]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4" data-testid="demo-landing">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Play className="h-8 w-8 text-amber-600 dark:text-amber-400 animate-pulse" />
          </div>
          <h1 className="text-xl font-semibold">Demo predstavitev</h1>
          <p className="text-sm text-muted-foreground">{status}</p>
          <div className="spinner mx-auto" />
          <p className="text-[11px] text-muted-foreground mt-4">
            Sekcija: <b>{rawSection}</b>{rawRole ? <> · Vloga: <b>{rawRole}</b></> : null}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoLanding;
